// Intent glyph for combat tokens — Slay the Spire's signature affordance
// applied to the D&D combat tracker. Each combatant displays an icon
// for what's coming next round, so players can plan against telegraphed
// state.
//
// MOBILE-REDESIGN.md §3a domain addendum: "Telegraph intent before
// commit. Slay the Spire's enemy intent icons (next-turn action shown
// above each token) are the same pattern. For us: combat actions show
// their effect before committing; creatures with readied actions show
// an intent glyph this round."
//
// Per Session 7 review-agent feasibility analysis, the data substrate
// for 3 of 4 intent kinds already exists today:
//
//   - concentrating       — char.concentratingOn
//   - incoming-damage     — combat.pendingDamage[].targetId
//   - ongoing-effect      — queueTicksForTiming output
//   - readied-action      — REQUIRES schema change (deferred to S7.6)
//
// Glyph kinds supported here are the first three. When the readied-
// action schema lands, add a `readied` case.
//
// Render: a small badge floats above (or beside) a combatant token.
// Icon + optional value label (e.g., "⚠ 12" for 12 incoming damage).
// Color-coded by kind: red for damage, blue for concentration, purple
// for ongoing effect. Tap to long-press receipt (the receipt
// integration is wired by the combat tracker, not by this component).

import React from 'react';

const KINDS = {
  'incoming-damage': {
    icon: '⚠',
    bg: 'rgba(212,64,64,0.85)',
    color: '#fff',
    border: '#ff4444',
    label: 'Incoming damage',
  },
  'concentrating': {
    icon: '🎯',
    bg: 'rgba(74,144,217,0.85)',
    color: '#fff',
    border: '#4a90d9',
    label: 'Concentrating',
  },
  'ongoing-effect': {
    icon: '⏳',
    bg: 'rgba(125,216,125,0.85)',
    color: '#0a0a0a',
    border: '#7dd87d',
    label: 'Ongoing effect',
  },
  'readied': {
    // Placeholder for the S7.6 schema addition. Renders today as a
    // muted gray badge so the wiring is in place when the data lands.
    icon: '⚡',
    bg: 'rgba(154,139,122,0.6)',
    color: '#fff',
    border: '#9a8b7a',
    label: 'Readied action',
  },
};

export default function IntentGlyph({ kind, value, title, onClick, style }) {
  const cfg = KINDS[kind];
  if (!cfg) return null;

  const ariaTitle = title || (value != null ? `${cfg.label}: ${value}` : cfg.label);

  return (
    <span
      role={onClick ? 'button' : 'img'}
      aria-label={ariaTitle}
      title={ariaTitle}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '2px 6px',
        minHeight: '20px',
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        color: cfg.color,
        borderRadius: '10px',
        fontSize: '0.7rem',
        fontWeight: 700,
        lineHeight: 1,
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
        ...style,
      }}
    >
      <span style={{ fontSize: '0.8rem' }}>{cfg.icon}</span>
      {value != null ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span> : null}
    </span>
  );
}

// Helper: given a combatant + current combat state + char data, return
// the list of intents to render above their token. Callers pass:
//   - combatant:    the combat.combatants entry { id, charId?, npcId?, ... }
//   - combat:       the full combat object (for pendingDamage scan)
//   - char:         the resolved character if combatant.charId set
//
// Returns: array of { kind, value?, key }
//
// The caller iterates and renders one <IntentGlyph> per entry. Empty
// array means no intents to telegraph this round (perfectly fine —
// not every combatant has something queued).
export function computeIntents(combatant, combat, char) {
  if (!combatant) return [];
  const intents = [];

  // Concentration — read from the resolved char doc.
  if (char && char.concentratingOn) {
    intents.push({
      kind: 'concentrating',
      key: 'concentrating',
      title: `Concentrating: ${char.concentratingOn}`,
    });
  }

  // Incoming pending damage — sum across queued entries targeting this combatant.
  if (combat && Array.isArray(combat.pendingDamage)) {
    const incoming = combat.pendingDamage.filter((p) => p && p.targetId === combatant.id);
    if (incoming.length > 0) {
      const totalAmount = incoming.reduce((sum, p) => {
        const amt = Number(p.amount) || 0;
        return sum + Math.abs(amt);
      }, 0);
      intents.push({
        kind: 'incoming-damage',
        value: totalAmount,
        key: 'incoming-damage',
        title: `Incoming damage: ${totalAmount} from ${incoming.length} source(s)`,
      });
    }
  }

  // Ongoing tick effects — scan the char's effectGroups for active DoTs / HoTs.
  // Filter on `tickEvery` only (not `duration`) so we don't flag every
  // short-duration buff (a 1-round Bless) as "ongoing" — the intent is
  // "queued ticks coming this round," not "any timed effect exists."
  // Per S8 review-agent feedback.
  if (char && Array.isArray(char.effectGroups)) {
    const ticking = char.effectGroups.filter((g) => g && g.tickEvery);
    if (ticking.length > 0) {
      intents.push({
        kind: 'ongoing-effect',
        value: ticking.length > 1 ? ticking.length : undefined,
        key: 'ongoing-effect',
        title: `Ongoing effects: ${ticking.map((g) => g.label || g.name || '').filter(Boolean).join(', ') || ticking.length}`,
      });
    }
  }

  return intents;
}
